import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Carousel.css";

/* All cards point to the Register page (same target as the Navbar CTA). */
const REGISTER_PATH = `${process.env.PUBLIC_URL || ""}/auth/register/form`;
/* Flagship module cards route to the subscription Plans page. */
const PLANS_PATH = `${process.env.PUBLIC_URL || ""}/plans`;

/* ------------------------------------------------------------------
   Line-art icons (stroke = currentColor). Add a case + reference it
   from a data row's `icon` field to introduce a new visual.
------------------------------------------------------------------ */
const Icon = ({ name }) => {
  const common = {
    viewBox: "0 0 24 24",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (name) {
    /* ---- flagship modules ---- */
    case "people":
      return (
        <svg {...common}>
          <circle cx="9" cy="7" r="3" />
          <path d="M3 21v-2a5 5 0 0 1 5-5h2" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M14 21v-1a4 4 0 0 1 4-4 4 4 0 0 1 4 4v1" />
        </svg>
      );
    case "cart":
      return (
        <svg {...common}>
          <circle cx="9" cy="20" r="1.5" />
          <circle cx="18" cy="20" r="1.5" />
          <path d="M2 3h3l2.5 12h10l2-8H6" />
        </svg>
      );

    /* ---- ecosystem steps ---- */
    case "layers":
      return (
        <svg {...common}>
          <path d="M12 3 3 8l9 5 9-5-9-5Z" />
          <path d="M3 13l9 5 9-5" />
        </svg>
      );
    case "link":
      return (
        <svg {...common}>
          <path d="M9 13a4 4 0 0 0 5.66 0l2.5-2.5a4 4 0 0 0-5.66-5.66l-1 1" />
          <path d="M15 11a4 4 0 0 0-5.66 0l-2.5 2.5a4 4 0 0 0 5.66 5.66l1-1" />
        </svg>
      );
    case "core":
      return (
        <svg {...common}>
          <rect x="6" y="6" width="12" height="12" rx="2.5" />
          <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
        </svg>
      );
    case "spark":
      return (
        <svg {...common}>
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
        </svg>
      );
    case "check":
      return (
        <svg {...common}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );

    /* ---- extra modules ---- */
    case "box":
      return (
        <svg {...common}>
          <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
          <path d="M3 8l9 5 9-5M12 13v8" />
        </svg>
      );
    case "procurement":
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 7h8M8 11h8M8 15h5" />
        </svg>
      );
    case "payroll":
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M6 9v6M18 9v6" />
        </svg>
      );

    /* ---- dashboards / BI / chart (default) ---- */
    case "bars":
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <rect x="7" y="11" width="3" height="6" />
          <rect x="13" y="7" width="3" height="10" />
        </svg>
      );
    case "chart":
    default:
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 14l3-4 3 3 4-6" />
        </svg>
      );
  }
};

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
);

/* ------------------------------------------------------------------
   Data — single source of truth for each block. Append a row to
   introduce a new step/module; no markup edits required.
------------------------------------------------------------------ */
const STEPS = [
  { n: "01", icon: "layers", title: "Business modules", text: "Seven modules for finance, people, sales and supply." },
  { n: "02", icon: "link", title: "Connected ecosystem", text: "Each module shares data instead of exporting it." },
  { n: "03", icon: "core", title: "Noser Core", text: "One transactional core — the single source of truth." },
  { n: "04", icon: "bars", title: "Unified dashboard", text: "Every number rolls up, live, in one view." },
  { n: "05", icon: "spark", title: "Business intelligence", text: "Insight built on data you can trace to source." },
  { n: "06", icon: "check", title: "Better decisions", text: "Act on what's true — not last month's export." },
];

const FLAGSHIPS = [
  {
    id: "financial",
    icon: "chart",
    tag: "FLAGSHIP MODULE",
    title: "Financial Accounting",
    text: "The general ledger at the center of the platform — real-time books, multi-entity consolidation, controls and audit trails.",
    features: ["Real-time ledger & close", "Multi-entity consolidation", "Approval controls & audit trail"],
  },
  {
    id: "hcm",
    icon: "people",
    tag: "FLAGSHIP MODULE",
    title: "Human Capital",
    text: "Manage people end to end — hiring, onboarding, time, and records — feeding straight into payroll and finance.",
    features: ["Hire-to-retire records", "Time & attendance", "Org & cost-center mapping"],
  },
  {
    id: "sales",
    icon: "cart",
    tag: "FLAGSHIP MODULE",
    title: "Sales & Operations",
    text: "Mobile point-of-sale, order management and a live operations dashboard, posting revenue to the ledger as it happens.",
    features: ["Mobile POS & orders", "Live ops dashboard", "Auto revenue posting"],
  },
];

const MODULES = [
  {
    id: "procurement",
    icon: "procurement",
    title: "Procurement",
    text: "Requisitions, purchase orders and vendor management with three-way matching against receipts and invoices.",
  },
  {
    id: "inventory",
    icon: "box",
    title: "Inventory",
    text: "Real-time stock across locations, with movements that reconcile automatically to cost of goods and the ledger.",
  },
  {
    id: "payroll",
    icon: "payroll",
    title: "Payroll",
    text: "Philippine-compliant payroll runs — taxes, contributions and payslips — driven by live HCM and timekeeping data.",
  },
  {
    id: "bi",
    icon: "bars",
    title: "Business Intelligence",
    text: "Dashboards and reports built on one dataset — every metric traces back to the transaction that produced it.",
  },
];

const Carousell = () => {
  const rootRef = useRef(null);

  /* Fade elements in when they enter the viewport and back out when they
     leave — re-triggers on every scroll (unlike the page's once-only AOS). */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll(".nz-reveal");

    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-in", entry.isIntersecting);
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="nz-eco" aria-label="How the platform fits together" ref={rootRef}>
      <div className="nz-eco__grid" aria-hidden="true" />

      {/* --- How it fits together --- */}
      <div className="nz-eco__inner">
        <span className="nz-eco__eyebrow nz-reveal">How it fits together</span>
        <h2 className="nz-eco__title nz-reveal" style={{ transitionDelay: "60ms" }}>
          From scattered modules
          <br />
          to better decisions
        </h2>
        <p className="nz-eco__lead nz-reveal" style={{ transitionDelay: "120ms" }}>
          The whole platform tells one story. Follow the thread from individual modules all the way to
          the decision on your desk.
        </p>

        <ol className="nz-eco__flow">
          {STEPS.map((s, i) => (
            <li
              key={s.n}
              className="nz-eco__step nz-card nz-glass nz-reveal"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <span className="nz-eco__num">{s.n}</span>
              <span className="nz-eco__chip">
                <Icon name={s.icon} />
              </span>
              <h3 className="nz-eco__step-title">{s.title}</h3>
              <p className="nz-eco__step-text">{s.text}</p>
              {i < STEPS.length - 1 && (
                <span className="nz-eco__connect" aria-hidden="true">
                  <ArrowRight />
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* --- Business modules --- */}
      <div className="nz-eco__inner nz-eco__inner--modules">
        <span className="nz-eco__eyebrow nz-reveal">Business modules</span>
        <div className="nz-eco__split">
          <h2 className="nz-eco__title nz-reveal" style={{ transitionDelay: "60ms" }}>
            Start with what you need. Connect the rest when you're ready.
          </h2>
          <p className="nz-eco__split-lead nz-reveal" style={{ transitionDelay: "120ms" }}>
            Each module is a complete product on its own. The advantage is that they were built on the
            same core — so adding the next one never means another integration project.
          </p>
        </div>

        <div className="nz-eco__flagships">
          {FLAGSHIPS.map((f, i) => (
            <article
              key={f.id}
              className="nz-eco__flag nz-card nz-glass nz-reveal"
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <div className="nz-eco__flag-head">
                <span className="nz-eco__chip">
                  <Icon name={f.icon} />
                </span>
                <span className="nz-eco__tag">{f.tag}</span>
              </div>
              <h3 className="nz-eco__flag-title">{f.title}</h3>
              <p className="nz-eco__flag-text">{f.text}</p>
              <ul className="nz-eco__features">
                {f.features.map((feat) => (
                  <li key={feat}>
                    <span className="nz-eco__feat-tick">
                      <Icon name="check" />
                    </span>
                    {feat}
                  </li>
                ))}
              </ul>
              <Link className="nz-eco__flag-link" to={PLANS_PATH}>
                See it in the ecosystem <ArrowRight />
              </Link>
            </article>
          ))}
        </div>

        <p className="nz-eco__more nz-reveal">+ four more modules on the same core</p>

        <div className="nz-eco__modules">
          {MODULES.map((m, i) => (
            <Link
              key={m.id}
              to={REGISTER_PATH}
              className="nz-eco__mod nz-card nz-glass nz-reveal"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <span className="nz-eco__chip nz-eco__chip--sm">
                <Icon name={m.icon} />
              </span>
              <div className="nz-eco__mod-body">
                <h4 className="nz-eco__mod-title">{m.title}</h4>
                <p className="nz-eco__mod-text">{m.text}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Carousell;
